import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { companyInfo } from '../mock';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setIsMobileMenuOpen(false);
      }
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navItems = [
    { labelKey: 'nav.home', path: '/', type: 'route' },
    { labelKey: 'nav.about', path: '/about', type: 'route' },
    { labelKey: 'nav.integratedHealthcare', path: '/integrated-healthcare', type: 'route' },
    { labelKey: 'nav.services', path: '/services', type: 'route' },
    { labelKey: 'nav.gallery', id: 'gallery', type: 'scroll' },
  ];

  return (
    <header
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: isScrolled ? 'rgba(10, 22, 40, 0.98)' : 'rgba(15, 29, 50, 0.95)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: isScrolled ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.3s ease',
      }}
    >
      <div
        className="container"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 1.2rem',
          position: 'relative',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Logo */}
        <div
          onClick={() => handleNavigation('/')}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          <img
            src="logo.png"
            alt={companyInfo.name}
            style={{
              height: '50px',
              width: 'auto',
              maxWidth: '100%',
            }}
          />
        </div>

        {/* Desktop Navigation */}
        <nav
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
          className="desktop-nav"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'nowrap' }}>
            {navItems.map((item) => (
              <button
                key={item.labelKey}
                onClick={() => item.type === 'route' ? handleNavigation(item.path) : scrollToSection(item.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#FFFFFF',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'color 0.2s ease',
                  padding: '0.5rem 0.6rem',
                  textShadow: '0 0 10px rgba(255, 255, 255, 0.3)',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = 'var(--tech-green-400)';
                  e.target.style.textShadow = '0 0 15px rgba(34, 197, 94, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#FFFFFF';
                  e.target.style.textShadow = '0 0 10px rgba(255, 255, 255, 0.3)';
                }}
              >
                {t(item.labelKey)}
              </button>
            ))}
          </div>
          <LanguageSwitcher />
          <button
            onClick={() => scrollToSection('contact')}
            className="btn-primary"
            style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
          >
            {t('nav.getStarted')}
          </button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            padding: '0.5rem',
            flexShrink: 0,
            zIndex: 1002,
            minWidth: '44px',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          className="mobile-menu-btn"
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'rgba(10, 22, 40, 0.98)',
            backdropFilter: 'blur(12px)',
            padding: '1.5rem',
          }}
          className="mobile-nav"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {navItems.map((item) => (
              <button
                key={item.labelKey}
                onClick={() => {
                  if (item.type === 'route') {
                    handleNavigation(item.path);
                  } else {
                    scrollToSection(item.id);
                  }
                  setIsMobileMenuOpen(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  padding: '0.75rem 0',
                  textAlign: 'left',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                {t(item.labelKey)}
              </button>
            ))}
            <div style={{ paddingTop: '1rem' }}>
              <LanguageSwitcher />
            </div>
            <button
              onClick={() => {
                scrollToSection('contact');
                setIsMobileMenuOpen(false);
              }}
              className="btn-primary"
              style={{ marginTop: '1rem' }}
            >
              {t('nav.getStarted')}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
